import OSS from 'ali-oss'

import { getAliyunOssPolicy } from '@/api/oss'

class OssClient {
  private static instance: OssClient | null = null
  private client: OSS | null = null
  private preUrl: string = ''
  private cdnUrl: string = ''
  private initialized: boolean = false

  private constructor() {}

  public static getInstance(): OssClient {
    if (!OssClient.instance) {
      OssClient.instance = new OssClient()
    }
    return OssClient.instance
  }

  /** 获取 OSS STS 配置 */
  private async getOssPolicy() {
    const res = await getAliyunOssPolicy()
    return res.data
  }

  /** 初始化 OSS 客户端（幂等，已初始化则跳过） */
  private async initClient(): Promise<void> {
    if (this.initialized) return

    const policy = await this.getOssPolicy()
    this.preUrl = policy?.preUrl ?? ''
    this.cdnUrl = policy?.cdnUrl ?? ''

    this.client = new OSS({
      region: 'oss-accelerate',
      accessKeyId: policy?.credentials?.accessKeyId,
      accessKeySecret: policy?.credentials?.accessKeySecret,
      stsToken: policy?.credentials?.securityToken,
      bucket: policy?.bucketName,
      /** STS 令牌过期前自动刷新 */
      refreshSTSToken: async () => {
        const res = await this.getOssPolicy()
        return {
          accessKeyId: res?.credentials?.accessKeyId,
          accessKeySecret: res?.credentials?.accessKeySecret,
          stsToken: res?.credentials?.securityToken
        }
      },
      refreshSTSTokenInterval: 10 * 60 * 1000
    })

    this.initialized = true
  }

  /**
   * 上传文件到 OSS
   * @param file 待上传的文件对象
   * @param dir  存储目录，默认 'uploads'（不含首尾斜杠）
   * @returns    可访问的 CDN 完整 URL
   */
  public async upload(file: File, dir = 'uploads'): Promise<string> {
    await this.initClient()

    if (!this.client) {
      throw new Error('OSS 客户端初始化失败')
    }

    // 生成唯一文件名：目录/时间戳_随机数.扩展名
    const ext = file.name.includes('.') ? file.name.split('.').pop() : ''
    const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}${ext ? '.' + ext : ''}`
    // 拼接后端约定的前置路径
    const objectKey = [this.preUrl, dir, filename].filter(Boolean).join('/')

    const result = await this.client.put(objectKey, file)

    // 优先使用 CDN 域名，否则回退 ali-oss 返回的 url
    if (this.cdnUrl) {
      return `${this.cdnUrl.replace(/\/$/, '')}/${objectKey}`
    }
    return result.url
  }
}

export const ossClient = OssClient.getInstance()
